import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Container,
  SimpleGrid,
  Paper,
  ThemeIcon,
  Group,
  Stack,
  Box,
  rem,
  ActionIcon,
  Divider,
} from '@mantine/core';
import {
  IconTruckDelivery,
  IconReportAnalytics,
  IconSettings,
  IconArrowUpRight,
  IconBuildingSkyscraper,
} from '@tabler/icons-react';

const heroGradient =
  'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

export default function ConcreteDashboard() {
  const navigate = useNavigate();

  // Modül yönlendirme kartlarının verileri
  // Not: 'path' kısımlarını kendi react-router-dom yapılandırmanıza göre güncelleyebilirsiniz.
  const modules = [
    {
      id: 'kayit',
      title: 'İrsaliye ve Fatura Kaydı',
      description: 'Beton santralinden çıkan araçların irsaliye, fatura ve metreküp bilgilerini sisteme işleyin.',
      icon: IconTruckDelivery,
      color: 'blue',
      path: '/beton-takip/kayit', 
    },
    {
      id: 'raporlar',
      title: 'Beton Takip Raporları',
      description: 'İç şantiye ve dış satış hareketlerini görüntüleyin, filtreleyin ve excel olarak dışa aktarın.',
      icon: IconReportAnalytics,
      color: 'green',
      path: '/beton-takip/raporlar',
    },
    {
      id: 'detayli-raporlar',
      title: 'Beton Takip Detaylı Raporlar',
      description: 'Günlük, Haftalık, Aylık, Beton Cinsi, İç şantiye ve dış satış hareketlerini detalı görüntüleyin ve filtreleyin',
      icon: IconReportAnalytics,
      color: 'red',
      path: '/beton-takip/detayli-raporlar',
    },
    {
      id: 'ayarlar',
      title: 'Sistem Ayarları',
      description: 'Beton cinsleri, fiyatlar, teslimat yapılacak şantiyeler ve dış firmaları yönetin.',
      icon: IconSettings,
      color: 'gray',
      path: '/beton-takip/ayarlar',
    },
  ];

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="xl">
        {/* HERO / BAŞLIK ALANI */}
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: heroGradient,
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
          }}
        >
          <Box
            style={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              filter: 'blur(4px)',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'rgba(96,165,250,0.18)',
              filter: 'blur(6px)',
            }}
          />

          <Stack gap="lg" style={{ position: 'relative', zIndex: 2 }}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Stack gap="sm">
                <Group gap="xs">
                  <ThemeIcon
                    size={38}
                    radius="xl"
                    variant="black"
                    color="rgba(255,255,255,0.14)"
                  >
                    <IconBuildingSkyscraper size={20} color="white" />
                  </ThemeIcon>
                  <Text size="sm" fw={600} c="rgba(255,255,255,0.82)">
                    Araç Filo & Şantiye Yönetimi
                  </Text>
                </Group>

                <Title
                  order={1}
                  fw={900}
                  style={{
                    color: 'white',
                    letterSpacing: '-1px',
                    lineHeight: 1.1,
                  }}
                >
                  Beton Takip Yönetimi
                </Title>

                <Text
                  size="md"
                  maw={760}
                  style={{
                    color: 'rgba(255,255,255,0.78)',
                    lineHeight: 1.7,
                  }}
                >
                  Beton döküm süreçlerinizi tek ekrandan yönetin. İrsaliye girişlerini yapın, sevkiyat raporlarını inceleyin ve sistem parametrelerini yapılandırın.
                </Text>
              </Stack>
            </Group>
          </Stack>
        </Paper>

        {/* MODÜL YÖNLENDİRME KARTLARI */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {modules.map((mod, index) => {
            const IconComponent = mod.icon;

            return (
              <Paper
                key={mod.id}
                withBorder
                radius="xl"
                p="lg"
                onClick={() => navigate(mod.path)}
                style={{
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 240,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid rgba(226,232,240,0.95)',
                  boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
                  transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
                  animation: `fadeUp 420ms ease ${index * 100}ms both`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 18px 40px rgba(15,23,42,0.10)';
                  e.currentTarget.style.borderColor = 'rgba(148,163,184,0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(15,23,42,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(226,232,240,0.95)';
                }}
              >
                {/* Kart içi renkli blur halkası */}
                <Box
                  style={{
                    position: 'absolute',
                    top: -30,
                    right: -30,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: `var(--mantine-color-${mod.color}-0)`,
                    opacity: 0.9,
                  }}
                />

                <Stack gap="md" style={{ position: 'relative', zIndex: 2 }}>
                  <Group justify="space-between" align="flex-start">
                    <ThemeIcon
                      size={58}
                      radius="xl"
                      variant="light"
                      color={mod.color}
                      style={{
                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.3)',
                      }}
                    >
                      <IconComponent style={{ width: rem(28), height: rem(28) }} stroke={1.7} />
                    </ThemeIcon>
                  </Group>

                  <div>
                    <Title order={4} fw={800} mb={8}>
                      {mod.title}
                    </Title>
                    <Text size="sm" c="dimmed" lh={1.7}>
                      {mod.description}
                    </Text>
                  </div>
                </Stack>

                <Stack gap={0}>
                  <Divider my="md" />
                  <Group justify="space-between" align="center">
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                      Modüle Git
                    </Text>
                    <ActionIcon
                      radius="xl"
                      size={36}
                      variant="light"
                      color={mod.color}
                    >
                      <IconArrowUpRight size={18} />
                    </ActionIcon>
                  </Group>
                </Stack>
              </Paper>
            );
          })}
        </SimpleGrid>

        <style>
          {`
            @keyframes fadeUp {
              from {
                opacity: 0;
                transform: translateY(14px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}
        </style>
      </Stack>
    </Container>
  );
}